import React, { useState } from "react";
import { Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "@/i18n/SiteContext";
import { api, errMsg } from "@/lib/api";
import { DatePicker, toISODate } from "@/components/site/DatePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PrivacyConsent } from "@/components/site/PrivacyConsent";

export const Label = ({ children }) => (
  <label className="block text-[11px] font-sans font-medium tracking-[0.2em] text-ocean/60 uppercase mb-2">{children}</label>
);

export const inputCls =
  "w-full h-12 px-4 bg-white border border-sand rounded-xl font-sans text-ocean text-sm placeholder:text-ocean/35 focus:outline-none focus:border-sea focus-visible:ring-2 focus-visible:ring-sand-brand/60";

const tomorrow = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 1);
  return d;
};

export const QuickRequestForm = () => {
  const { t, lang } = useSite();
  const [f, setF] = useState({ name: "", email: "", guests: "", interests: "" });
  const [from, setFrom] = useState();
  const [to, setTo] = useState();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [privacy, setPrivacy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email) return toast.error(t("err.required"));
    if (!privacy) return toast.error(t("consent.required"));
    setLoading(true);
    try {
      await api.post("/inquiries", {
        kind: "dates",
        name: f.name,
        email: f.email,
        guests: f.guests,
        interests: f.interests,
        message: f.interests,
        date_from: toISODate(from),
        date_to: toISODate(to),
        lang,
        privacy_accepted: true,
      });
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err, t("err.generic")));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-10" data-testid="quick-request-success">
        <CheckCircle2 size={44} className="mx-auto text-sea" />
        <h3 className="font-serif text-3xl text-ocean mt-5">{t("dates.sentTitle")}</h3>
        <p className="mt-3 text-ocean/60 max-w-md mx-auto">{t("dates.sentText")}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="quick-request-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label>{t("dates.name")} *</Label>
          <input required className={inputCls} placeholder="Maria Rossi" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="quick-request-name" />
        </div>
        <div>
          <Label>{t("dates.email")} *</Label>
          <input required type="email" className={inputCls} placeholder="maria@example.com" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="quick-request-email" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <Label>{t("dates.from")}</Label>
          <DatePicker value={from} onChange={setFrom} placeholder={t("dates.pickDate")} testId="quick-request-from" minDate={tomorrow()} />
        </div>
        <div>
          <Label>{t("dates.to")}</Label>
          <DatePicker value={to} onChange={setTo} placeholder={t("dates.pickDate")} testId="quick-request-to" minDate={from || tomorrow()} />
        </div>
        <div>
          <Label>{t("dates.guests")}</Label>
          <Select value={f.guests} onValueChange={(v) => setF({ ...f, guests: v })}>
            <SelectTrigger className="h-12 rounded-xl border-sand bg-white text-ocean" data-testid="quick-request-guests">
              <SelectValue placeholder={t("dates.howMany")} />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {["1", "2", "3-4", "5-8", "9+"].map((g) => (
                <SelectItem key={g} value={g} data-testid={`quick-request-guests-${g}`}>{g}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>{t("dates.prefs")}</Label>
        <textarea rows={3} className={`${inputCls} h-auto py-3 resize-none`} placeholder={t("dates.prefsPh")} value={f.interests} onChange={(e) => setF({ ...f, interests: e.target.value })} data-testid="quick-request-preferences" />
      </div>
      <PrivacyConsent checked={privacy} onChange={setPrivacy} testId="quick-request-privacy" />
      <button type="submit" disabled={loading} data-testid="quick-request-submit" className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-ocean text-ivory font-medium text-sm tracking-wider uppercase hover:bg-ocean-700 disabled:opacity-60 rounded-xl shadow-soft btn-lift">
        <Send size={17} /> {loading ? t("dates.sending") : t("dates.send")}
      </button>
    </form>
  );
};
